import { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from 'src/store/hooks'
import { selectSelectedLinkedService } from '../../create.slice'

type HasLibraryFields = {
  linkedServiceID: string
  name: string
  courses: { courseID: string; courseName: string }[]
}

type UseLibraryFiltersResult<T> = {
  linkedServiceFilter: string
  courseFilter: string
  search: string
  setLinkedServiceFilter: (v: string) => void
  setCourseFilter: (v: string) => void
  setSearch: (v: string) => void
  resetFilters: () => void
  isFiltered: boolean
  filteredItems: T[]
  distinctCourses: { courseID: string; courseName: string }[]
}

export function useLibraryFilters<T extends HasLibraryFields>(
  items: T[],
): UseLibraryFiltersResult<T> {
  let [linkedServiceFilter, setLinkedServiceFilter] = useState('all')
  let [courseFilter, setCourseFilter] = useState('all')
  let [search, setSearch] = useState('')

  let activeLinkedService = useAppSelector(selectSelectedLinkedService)

  useEffect(() => {
    if (activeLinkedService && activeLinkedService !== 'unselected') {
      setLinkedServiceFilter(activeLinkedService)
    }
  }, [activeLinkedService])

  let isFiltered =
    linkedServiceFilter !== 'all' || courseFilter !== 'all' || search !== ''

  let resetFilters = () => {
    setLinkedServiceFilter('all')
    setCourseFilter('all')
    setSearch('')
  }

  let distinctCourses = useMemo(() => {
    let seen = new Map<string, string>()
    items
      .filter(
        (item) =>
          linkedServiceFilter === 'all' ||
          item.linkedServiceID === linkedServiceFilter,
      )
      .forEach((item) =>
        item.courses.forEach((c) => seen.set(c.courseID, c.courseName)),
      )
    return Array.from(seen.entries())
      .map(([courseID, courseName]) => ({ courseID, courseName }))
      .toSorted((a, b) => a.courseName.localeCompare(b.courseName))
  }, [items, linkedServiceFilter])

  let filteredItems = useMemo(
    () =>
      items.filter((item) => {
        if (
          linkedServiceFilter !== 'all' &&
          item.linkedServiceID !== linkedServiceFilter
        )
          return false
        if (courseFilter === 'none' && item.courses.length > 0) return false
        if (
          courseFilter !== 'all' &&
          courseFilter !== 'none' &&
          !item.courses.some((c) => c.courseID === courseFilter)
        )
          return false
        if (search && !item.name.toLowerCase().includes(search.toLowerCase()))
          return false
        return true
      }),
    [items, linkedServiceFilter, courseFilter, search],
  )

  return {
    linkedServiceFilter,
    courseFilter,
    search,
    setLinkedServiceFilter,
    setCourseFilter,
    setSearch,
    resetFilters,
    isFiltered,
    filteredItems,
    distinctCourses,
  }
}
